import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Card,
  Box,
  Stack,
  Typography,
  Grid,
  Slider,
  IconButton,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';

// React Player
import ReactPlayer from 'react-player';

// Icons
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

export default function MallManagementTutorialPage() {
  // Statik video verileri
  const [videos] = useState([
    {
      id: 'mall-management-goods-acceptance',
      title: 'Mal Kabul',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/inbound-sv.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-correspondence',
      title: 'Muhaberat',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/ml.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-click-collect',
      title: 'Click&Collect',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/click-collect.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-handsfree',
      title: 'Handsfree',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/hands-free-2.vs.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-tax-free',
      title: 'Tax Free',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/taxfree-16-9-ing.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-cargo',
      title: 'Kargo',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/ep.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-safety-locker',
      title: 'Emanet Dolabı',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/ep.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-home-delivery',
      title: 'Eve Teslimat',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/home-delivery.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-safety-cloakroom',
      title: 'Emanet - Vestiyer',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/ep.mp4',
      type: 'mall-management',
    },
    {
      id: 'mall-management-postbox',
      title: 'Postbox',
      url: 'https://files.easypoint.com.tr/files/data-ekranlari/posta-kutusu.mp4',
      type: 'mall-management',
    },
  ]);



  // Video oynatma durumları
  const [selectedVideoId, setSelectedVideoId] = useState(videos[0]?.id || null);
  const [playing, setPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeout = useRef(null);

  // Zaman çubuğu için progress (0-100)
  const [played, setPlayed] = useState(0);

  // Video süresi ve anlık zamanı için state'ler
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Her video için hesaplanan süreleri saklamak adına state
  const [videoDurations, setVideoDurations] = useState({});

  // Fullscreen durumu
  const [isFullScreen, setIsFullScreen] = useState(false);

  // ReactPlayer referansı
  const playerRef = useRef(null);
  // Fullscreen için container referansı
  const containerRef = useRef(null);

  // Yardımcı: Süreyi mm:ss formatına çevirme
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Seçilen video değiştiğinde süre bilgilerini sıfırlıyoruz
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setPlayed(0);
  }, [selectedVideoId]);

  // Fare hareketinde kontrolleri göster
  const handleMouseMove = () => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  // Play/Pause butonu
  const handlePlayPause = () => {
    setPlaying((prev) => !prev);
  };

  // Fullscreen aç/kapa
  const handleToggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (containerRef.current) {
        containerRef.current.requestFullscreen();
      }
    } else {
      document.exitFullscreen();
    }
  };

  // Fullscreen değişiminde state güncellemesi
  const handleFullScreenChange = useCallback(() => {
    const fsElement = document.fullscreenElement;
    setIsFullScreen(!!fsElement);
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [handleFullScreenChange]);

  // Component unmount'ta timeout temizliği
  useEffect(() => {
    return () => {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, []);

  // ReactPlayer onProgress callback'ı
  const handleProgress = (state) => {
    setPlayed(state.played * 100);
    setCurrentTime(state.playedSeconds);
  };

  // Video ikonuna tıklanınca
  const handleVideoIconClick = (vid) => {
    if (vid.id === selectedVideoId) {
      setPlaying((prev) => !prev);
    } else {
      setSelectedVideoId(vid.id);
      setPlaying(true);
      setPlayed(0);
      setCurrentTime(0);
      setDuration(0);
    }
  };

  // Videoların süresini önceden yüklemek için yardımcı fonksiyon
  const loadVideoDurations = (videosArray) => {
    videosArray.forEach((video) => {
      const videoEl = document.createElement('video');
      videoEl.src = video.url;
      videoEl.preload = 'metadata';
      videoEl.addEventListener('loadedmetadata', () => {
        setVideoDurations((prev) => ({
          ...prev,
          [video.id]: videoEl.duration,
        }));
      });
      videoEl.addEventListener('error', () => {
        setVideoDurations((prev) => ({
          ...prev,
          [video.id]: 0,
        }));
      });
    });
  };

  // Video sürelerini yükle
  useEffect(() => {
    loadVideoDurations(videos);
  }, []);

  // Seçili video objeleri
  const selectedVideo = videos.find((vid) => vid.id === selectedVideoId);

  return (
    <>
      <Helmet>
        <title>AVM Yönetimi Eğitim Dokümanı</title>
      </Helmet>
      <Stack p={3} spacing={2}>
        {/* Üst Başlık */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h4">AVM Yönetimi Eğitim Dokümanı</Typography>
        </Stack>
        {/* Ana içerik */}
        <Grid container spacing={2}>
          {/* Sol Panel */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              {/* İçerik Listesi */}
              <Card
                sx={{
                  position: 'relative',
                  backgroundColor: '#F5F5F5',
                  height: '500px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  px: '20px',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                    marginBlock: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#CFCFCF',
                    borderRadius: '4px',
                    border: '2px solid transparent',
                    backgroundClip: 'content-box',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#BFBFBF',
                  },
                  mr: '-8px',
                  pr: '8px',
                  py: '8px',
                }}
              >
                <Stack direction="row" alignItems="center" mb={2}>
                  <OndemandVideoIcon sx={{ color: '#8D8A8A', mr: 1 }} />
                  <Typography sx={{ color: '#2C2C2C', fontSize: '18px' }}>
                    {videos.length > 0
                      ? `${videos.length} AVM Yönetimi Eğitim Videosu`
                      : 'Video bulunmamaktadır'}
                  </Typography>
                </Stack>
                {videos.length > 0 ? (
                  videos.map((vid) => (
                    <Box
                      key={vid.id}
                      onClick={() => {
                        if (vid.id !== selectedVideoId) {
                          setSelectedVideoId(vid.id);
                          setPlaying(false);
                          setPlayed(0);
                          setCurrentTime(0);
                          setDuration(0);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        py: 2,
                        borderRadius: 1,
                        mb: '20px',
                        backgroundColor:
                          selectedVideoId === vid.id ? '#0666F6' : '#FFFFFF',
                        color:
                          selectedVideoId === vid.id ? '#F9F9F9' : '#2C2C2C',
                        fontWeight: 400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoIconClick(vid);
                        }}
                      >
                        {selectedVideoId === vid.id && playing ? (
                          <PauseIcon
                            sx={{
                              fontSize: 20,
                              color:
                                selectedVideoId === vid.id
                                  ? '#F9F9F9'
                                  : '#2C2C2C',
                            }}
                          />
                        ) : (
                          <PlayArrowIcon
                            sx={{
                              fontSize: 20,
                              color:
                                selectedVideoId === vid.id
                                  ? '#F9F9F9'
                                  : '#2C2C2C',
                            }}
                          />
                        )}
                      </IconButton>
                      <Typography sx={{ flex: 1, marginLeft: 1 }}>
                        {vid.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            selectedVideoId === vid.id ? 'white' : '#666',
                          fontSize: '14px',
                        }}
                      >
                        {videoDurations[vid.id]
                          ? formatTime(videoDurations[vid.id])
                          : '0:00'}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography>Video bulunmamaktadır</Typography>
                )}
              </Card>
            </Stack>
          </Grid>
          {/* Sağ Panel: Video Gösterici */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                height: '600px',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
              }}
              ref={containerRef}
              onMouseMove={handleMouseMove}
            >
                <Stack direction="row" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedVideo?.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    {formatTime(duration)}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    flex: 1,
                    mb: 1,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {selectedVideo && (
                    <>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          left: 10,
                          padding: '4px 8px',
                          background: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                          borderRadius: '4px',
                          zIndex: 10,
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                      >
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </Box>
                      <ReactPlayer
                        key={selectedVideo.id}
                        ref={playerRef}
                        url={selectedVideo.url}
                        playing={playing}
                        width="100%"
                        height="100%"
                        controls={false}
                        onProgress={handleProgress}
                        onDuration={(dur) => {
                          setDuration(dur);
                          setVideoDurations((prev) => ({
                            ...prev,
                            [selectedVideo.id]: dur,
                          }));
                        }}
                      />
                      {showControls && (
                        <Box
                          onClick={handlePlayPause}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            cursor: 'pointer',
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background:
                              'radial-gradient(circle, rgba(255,255,255,1) 40%, rgba(255,255,255,0.2) 70%, transparent 100%)',
                            boxShadow: '0 0 0 3px rgba(255,255,255,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {playing ? (
                            <PauseIcon
                              sx={{ fontSize: 38, color: '#0666F6' }}
                            />
                          ) : (
                            <PlayArrowIcon
                              sx={{ fontSize: 38, color: '#0666F6' }}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Slider
                    min={0}
                    max={100}
                    value={played}
                    onChange={(_, val) => {
                      setPlayed(val);
                      if (playerRef.current) {
                        playerRef.current.seekTo(val / 100);
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton onClick={handleToggleFullScreen}>
                    {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                </Stack>
                <Typography variant="body1" sx={{ color: '#444', mt: 2 }}>
                  {selectedVideo?.description}
                </Typography>
              </Card>
          </Grid>
        </Grid>
      </Stack>
    </>
  );
}


